import * as React from 'react'
import {
  PLANT_PAINT_CONTAINMENT_PARENT_ENDPOINT,
  PLANT_PAINT_GHOST_PARENT_ENDPOINT,
  PLANT_PAINT_GHOST_TYPE_PRECEDENCE
} from '@atbash-labs/ontology-core'

import type { Branch, TaggedNode, TaggedRel } from '../branches/types'
import { decodeTaggedProps } from '../branches/delta-codec'
import {
  useGetBranchIngestChangesetQuery,
  useGetBranchIngestModificationsQuery,
  useGetBranchIngestWriteLogWatermarkQuery,
  useGetIngestChangesetQuery,
  useGetIngestModificationsQuery,
  useGetIngestWriteLogWatermarkQuery,
  useListIngestSectionsQuery,
  type BranchIngestJobSummary,
  type IngestChangeset,
  type IngestChangesetEntry,
  type IngestModifications,
  type IngestSection,
  type IngestSessionEvidence
} from '../ingest/ingest-api'
import { useSessionPaintBranchContext, type IngestSession } from '../ingest/session-presence'
import { useAppDispatch, useAppSelector } from '../../hooks'
import {
  bindSessionPaintBranch,
  selectSessionPaintEnabled,
  selectSessionPaintSelection,
  setSessionPaintEnabled,
  setSessionPaintSelection,
  type SessionPaintSelection
} from '../../slices/session-paint-scope-slice'
import {
  activeSections,
  nodeSectionIds,
  remapSectionIds,
  resolveActiveSectionId
} from './section-filter'

export type PlantSessionMark = 'added' | 'touched' | 'deleted'
export type PlantSessionMarkView = PlantSessionMark | 'dimmed'

export interface PlantSessionMarkEntry {
  mark: PlantSessionMark
  name?: string
  sections: string[]
  jobLevel: boolean
  /** Graph labels from the write-log row (e.g. `Equipment`, `Line`). */
  labels: string[]
}

export interface PlantSessionRelationshipMarkEntry {
  mark: PlantSessionMark
  relationship: TaggedRel
  sections: string[]
  jobLevel: boolean
}

/** Deleted node placed on a container page via a containment edge. */
export interface PlantSessionGhost {
  id: string
  name?: string
  labels: string[]
}

export type PlantPaintSelection = SessionPaintSelection

export interface PlantPaintFilter {
  selection: PlantPaintSelection
  setSelection: (selection: PlantPaintSelection) => void
  enabled: boolean
  setEnabled: (enabled: boolean) => void
}

export interface PlantPaintTotals {
  added: number
  touched: number
  deleted: number
  relsAdded: number
  relsDeleted: number
}

export interface PlantSessionPaint {
  active: boolean
  branchId: string | undefined
  jobId: string | undefined
  session: IngestSession | undefined
  agentOnly: boolean
  hasAgentEdits: boolean
  /** Entity mark count under the agent-domain fold (same units as All changes). */
  agentChangesCount: number
  jobsSummary: BranchIngestJobSummary[]
  agentJobs: BranchIngestJobSummary[]
  sections: IngestSection[]
  /** Unfiltered branch-union marks, used for stable dropdown counts. */
  branchMarkById: Map<string, PlantSessionMarkEntry>
  branchRelationshipMarkById: Map<string, PlantSessionRelationshipMarkEntry>
  markById: Map<string, PlantSessionMarkEntry>
  relationshipMarkById: Map<string, PlantSessionRelationshipMarkEntry>
  ghostRels: TaggedRel[]
  /** Deleted nodes keyed by immediate containment parent (unplaceable omitted). */
  ghostsByContainerId: Map<string, PlantSessionGhost[]>
  /** Added descendants of any label (lines, equipment, …) under each container. */
  rollupByContainerId: Map<string, number>
  /** Touched / property-updated descendants under each container (`~n` pills). */
  touchedRollupByContainerId: Map<string, number>
  /** Deleted descendants of any label under each container. */
  deletedRollupByContainerId: Map<string, number>
  /** Added `:Equipment` descendants only — for Equipment stat deltas. */
  equipmentRollupByContainerId: Map<string, number>
  /** Touched `:Equipment` descendants only — for Equipment `~n` deltas. */
  equipmentTouchedRollupByContainerId: Map<string, number>
  /** Deleted `:Equipment` descendants only — for Equipment `−n` deltas. */
  equipmentDeletedRollupByContainerId: Map<string, number>
  totals: PlantPaintTotals
  /** Property-level net changeset, keyed by entity id — the job-wide fold (tier-2). */
  changes: ReadonlyMap<string, IngestChangesetEntry>
  /**
   * Session-local source citations keyed by node id. Filtered by the active
   * paint lens; empty when paint is inactive or disabled.
   */
  sourceEvidence: ReadonlyMap<string, IngestSessionEvidence[]>
  filter: PlantPaintFilter
  markFor: (id: string) => PlantSessionMarkView | undefined
  relationshipMarkFor: (id: string) => PlantSessionMarkView | undefined
  inactiveSectionChangeCount: number
  /** Marks were capped while exact totals still cover the full write-log fold. */
  truncated: boolean
}

export const PlantPaintContext = React.createContext<PlantSessionPaint | undefined>(undefined)

function nodeName(node: TaggedNode): string | undefined {
  try {
    const name = decodeTaggedProps(node.properties ?? {}).name
    return typeof name === 'string' && name.length > 0 ? name : undefined
  } catch {
    return undefined
  }
}

/** Build the net per-node marks. Delete rows overwrite live rows by design. */
export function buildMarkMap(
  modifications: IngestModifications | undefined
): Map<string, PlantSessionMarkEntry> {
  const marks = new Map<string, PlantSessionMarkEntry>()
  if (modifications?.hasLog !== true) return marks

  const createdIds =
    modifications.createdNodeIds === undefined ? undefined : new Set(modifications.createdNodeIds)
  const jobLevelIds = new Set(modifications.jobLevelNodeIds ?? [])
  for (const node of modifications.nodes ?? []) {
    marks.set(node.id, {
      mark: createdIds === undefined || createdIds.has(node.id) ? 'added' : 'touched',
      name: nodeName(node),
      sections: nodeSectionIds(node, modifications.sectionsByNode),
      jobLevel: jobLevelIds.has(node.id),
      labels: node.labels ?? []
    })
  }
  for (const node of modifications.deletedNodes ?? []) {
    marks.set(node.id, {
      mark: 'deleted',
      name: nodeName(node),
      sections: nodeSectionIds(node, modifications.sectionsByNode),
      jobLevel: jobLevelIds.has(node.id),
      labels: node.labels ?? []
    })
  }
  return marks
}

/** Index the job's changeset entries by entity id — tier-2 property rows. */
export function buildChangesById(
  changeset: IngestChangeset | undefined
): Map<string, IngestChangesetEntry> {
  const changes = new Map<string, IngestChangesetEntry>()
  if (changeset?.hasLog !== true) return changes
  for (const entry of changeset.entries ?? []) changes.set(entry.id, entry)
  return changes
}

/** Build net relationship marks with the same section/job attribution as nodes. */
export function buildRelationshipMarkMap(
  modifications: IngestModifications | undefined
): Map<string, PlantSessionRelationshipMarkEntry> {
  const marks = new Map<string, PlantSessionRelationshipMarkEntry>()
  if (modifications?.hasLog !== true) return marks

  const createdIds =
    modifications.createdRelationshipIds === undefined
      ? undefined
      : new Set(modifications.createdRelationshipIds)
  const jobLevelIds = new Set(modifications.jobLevelRelationshipIds ?? [])
  for (const relationship of modifications.relationships ?? []) {
    marks.set(relationship.id, {
      mark: createdIds === undefined || createdIds.has(relationship.id) ? 'added' : 'touched',
      relationship,
      sections: modifications.sectionsByRelationship?.[relationship.id] ?? [],
      jobLevel: jobLevelIds.has(relationship.id)
    })
  }
  for (const relationship of modifications.deletedRelationships ?? []) {
    marks.set(relationship.id, {
      mark: 'deleted',
      relationship,
      sections: modifications.sectionsByRelationship?.[relationship.id] ?? [],
      jobLevel: jobLevelIds.has(relationship.id)
    })
  }
  return marks
}

function remapMarkSections(
  marks: ReadonlyMap<string, PlantSessionMarkEntry>,
  sections: readonly IngestSection[] | undefined
): Map<string, PlantSessionMarkEntry> {
  return new Map(
    [...marks].map(([id, entry]) => [
      id,
      { ...entry, sections: remapSectionIds(entry.sections, sections) }
    ])
  )
}

function remapRelationshipMarkSections(
  marks: ReadonlyMap<string, PlantSessionRelationshipMarkEntry>,
  sections: readonly IngestSection[] | undefined
): Map<string, PlantSessionRelationshipMarkEntry> {
  return new Map(
    [...marks].map(([id, entry]) => [
      id,
      { ...entry, sections: remapSectionIds(entry.sections, sections) }
    ])
  )
}

/** Multi-parent climb map — a node may sit under Line and Area at once. */
function buildParentsByChild(
  relationships: readonly TaggedRel[],
  endpoints: Readonly<Record<string, 'start' | 'end'>> = PLANT_PAINT_CONTAINMENT_PARENT_ENDPOINT
): Map<string, Set<string>> {
  const parentsByChild = new Map<string, Set<string>>()
  for (const relationship of relationships) {
    const parentEndpoint = endpoints[relationship.type]
    if (!parentEndpoint) continue
    const parentId = parentEndpoint === 'start' ? relationship.startId : relationship.endId
    const childId = parentEndpoint === 'start' ? relationship.endId : relationship.startId
    const parents = parentsByChild.get(childId)
    if (parents) parents.add(parentId)
    else parentsByChild.set(childId, new Set([parentId]))
  }
  return parentsByChild
}

/**
 * Pick one ghost parent when multiple containment edges exist. Earlier types in
 * {@link PLANT_PAINT_GHOST_TYPE_PRECEDENCE} win (Factory before Area `LOCATED_IN`).
 */
function pickGhostParent(
  childId: string,
  relationships: readonly TaggedRel[],
  endpoints: Readonly<Record<string, 'start' | 'end'>> = PLANT_PAINT_GHOST_PARENT_ENDPOINT
): string | undefined {
  let bestParent: string | undefined
  let bestRank = Number.POSITIVE_INFINITY
  for (const relationship of relationships) {
    const parentEndpoint = endpoints[relationship.type]
    if (!parentEndpoint) continue
    const parentId = parentEndpoint === 'start' ? relationship.startId : relationship.endId
    const relChildId = parentEndpoint === 'start' ? relationship.endId : relationship.startId
    if (relChildId !== childId) continue
    const rank =
      PLANT_PAINT_GHOST_TYPE_PRECEDENCE.get(relationship.type) ?? Number.POSITIVE_INFINITY
    if (rank < bestRank) {
      bestRank = rank
      bestParent = parentId
    }
  }
  return bestParent
}

/**
 * Count distinct marked descendants under each container. Parent chains are
 * walked (multi-parent) so one equipment contributes to its Line and Area
 * pills, and once to a Site even when both paths converge.
 * Pass `onlyLabels` to count only nodes carrying one of those graph labels
 * (e.g. `['Equipment']` for Equipment stat deltas). Defaults to `added`.
 */
export function buildRollups(
  marks: ReadonlyMap<string, PlantSessionMarkEntry>,
  relationships: readonly TaggedRel[],
  options?: { onlyLabels?: readonly string[]; mark?: PlantSessionMark }
): Map<string, number> {
  const parentsByChild = buildParentsByChild(relationships)
  const targetMark = options?.mark ?? 'added'
  const onlyLabels = options?.onlyLabels
  const rollups = new Map<string, number>()
  for (const [id, entry] of marks) {
    if (entry.mark !== targetMark) continue
    if (onlyLabels && !onlyLabels.some((label) => entry.labels.includes(label))) continue
    const visited = new Set<string>([id])
    const queue = [...(parentsByChild.get(id) ?? [])]
    while (queue.length > 0) {
      const parentId = queue.shift()
      if (!parentId || visited.has(parentId)) continue
      visited.add(parentId)
      rollups.set(parentId, (rollups.get(parentId) ?? 0) + 1)
      for (const grandId of parentsByChild.get(parentId) ?? []) {
        if (!visited.has(grandId)) queue.push(grandId)
      }
    }
  }
  return rollups
}

/**
 * Place deleted nodes on their immediate containment parent. Unplaceable
 * deletes (no resolvable parent edge) are omitted — they still count in totals.
 * Pass live + deleted relationships so containment can come from either list.
 * When multiple parents exist, Factory edges win over Area `LOCATED_IN`.
 */
export function buildGhostsByContainer(
  marks: ReadonlyMap<string, PlantSessionMarkEntry>,
  relationships: readonly TaggedRel[]
): Map<string, PlantSessionGhost[]> {
  const byContainer = new Map<string, PlantSessionGhost[]>()
  for (const [id, entry] of marks) {
    if (entry.mark !== 'deleted') continue
    const parentId = pickGhostParent(id, relationships)
    if (!parentId) continue
    const ghost: PlantSessionGhost = { id, name: entry.name, labels: entry.labels }
    const list = byContainer.get(parentId)
    if (list) list.push(ghost)
    else byContainer.set(parentId, [ghost])
  }
  return byContainer
}

export function applyPaintFilter(
  entry:
    | Pick<PlantSessionMarkEntry, 'mark' | 'sections' | 'jobLevel'>
    | Pick<PlantSessionRelationshipMarkEntry, 'mark' | 'sections' | 'jobLevel'>
    | undefined,
  selection: PlantPaintSelection,
  enabled: boolean
): PlantSessionMarkView | undefined {
  if (!enabled) return undefined
  if (!entry) return undefined
  if (selection.kind === 'all' || selection.kind === 'agent' || selection.kind === 'sourceJob') {
    return entry.mark
  }
  if (selection.kind === 'jobLevel') return entry.jobLevel ? entry.mark : 'dimmed'
  return entry.sections.includes(selection.sectionId) ? entry.mark : 'dimmed'
}

/** Compatibility helper for section-only callers and pure tests. */
export function applySectionFilter(
  entry: PlantSessionMarkEntry | undefined,
  sectionId: string | null,
  enabled: boolean
): PlantSessionMarkView | undefined {
  return applyPaintFilter(
    entry,
    sectionId ? { kind: 'section', sectionId } : { kind: 'all' },
    enabled
  )
}

/** Keep only marks that contribute to the selected scope. */
export function filterMarksForSelection(
  marks: ReadonlyMap<string, PlantSessionMarkEntry>,
  selection: PlantPaintSelection,
  enabled: boolean
): Map<string, PlantSessionMarkEntry> {
  const filtered = new Map<string, PlantSessionMarkEntry>()
  for (const [id, entry] of marks) {
    const view = applyPaintFilter(entry, selection, enabled)
    if (view === entry.mark) filtered.set(id, entry)
  }
  return filtered
}

export function filterRelationshipMarksForSelection(
  marks: ReadonlyMap<string, PlantSessionRelationshipMarkEntry>,
  selection: PlantPaintSelection,
  enabled: boolean
): Map<string, PlantSessionRelationshipMarkEntry> {
  const filtered = new Map<string, PlantSessionRelationshipMarkEntry>()
  for (const [id, entry] of marks) {
    const view = applyPaintFilter(entry, selection, enabled)
    if (view === entry.mark) filtered.set(id, entry)
  }
  return filtered
}

export function resolvePlantPaintSelection(
  selection: PlantPaintSelection,
  sections: readonly IngestSection[] | undefined,
  jobsSummary?: readonly BranchIngestJobSummary[]
): PlantPaintSelection {
  // Actor jobs stay conversation-keyed in storage; the Plant bar exposes one
  // aggregate agent lens. Coerce any sourceJob pointing at an actor job.
  if (selection.kind === 'sourceJob') {
    if (jobsSummary !== undefined) {
      const agentJobIds = new Set(agentJobsFromSummary(jobsSummary).map((job) => job.jobId))
      if (agentJobIds.has(selection.jobId)) return { kind: 'agent' }
    }
    return selection
  }
  if (selection.kind === 'agent') return selection
  if (selection.kind !== 'section' || sections === undefined) return selection
  const sectionId = resolveActiveSectionId(selection.sectionId, sections)
  return sectionId ? { kind: 'section', sectionId } : { kind: 'all' }
}

export function agentJobsFromSummary(
  jobs: readonly BranchIngestJobSummary[] | undefined
): BranchIngestJobSummary[] {
  return (jobs ?? []).filter((job) => job.domain === 'agent' || job.kind === 'agent_edit')
}

/** Filter selection used for mark dimming — job lenses fold at the query layer. */
export function paintFilterSelection(selection: PlantPaintSelection): PlantPaintSelection {
  if (selection.kind === 'sourceJob' || selection.kind === 'agent') return { kind: 'all' }
  return selection
}

/**
 * Branch-union modifications args. Pass the identity session job as
 * `sectionsJobId` so section / job-level membership stays scoped to that
 * catalog; omit it for agent-only branches (no sections).
 */
export function branchPaintModificationsArgs(
  branchId: string,
  sectionsJobId: string | undefined
): { branchId: string; sectionsJobId?: string } {
  return sectionsJobId ? { branchId, sectionsJobId } : { branchId }
}

/**
 * Branch-union changeset args. Agent lens skips `sectionsJobId` (no section
 * catalog); section lenses pass both `sectionId` and the identity job.
 */
export function branchPaintChangesetArgs(input: {
  branchId: string
  isAgentLens: boolean
  sectionId?: string
  sectionsJobId?: string
}): { branchId: string; domain?: 'agent'; sectionId?: string; sectionsJobId?: string } {
  return {
    branchId: input.branchId,
    ...(input.isAgentLens ? { domain: 'agent' as const } : {}),
    ...(input.sectionId ? { sectionId: input.sectionId } : {}),
    ...(!input.isAgentLens && input.sectionsJobId ? { sectionsJobId: input.sectionsJobId } : {})
  }
}

function citationMatchesSelection(
  citation: IngestSessionEvidence,
  selection: PlantPaintSelection
): boolean {
  if (selection.kind === 'all' || selection.kind === 'agent' || selection.kind === 'sourceJob') {
    return true
  }
  if (selection.kind === 'jobLevel') return citation.jobLevel
  return citation.sections.includes(selection.sectionId)
}

/**
 * Build the session-local source evidence map from a modifications payload,
 * remapping merged section ids and filtering by the active paint lens.
 */
export function buildSourceEvidenceMap(
  modifications: IngestModifications | undefined,
  sections: readonly IngestSection[] | undefined,
  selection: PlantPaintSelection,
  enabled: boolean
): Map<string, IngestSessionEvidence[]> {
  const out = new Map<string, IngestSessionEvidence[]>()
  if (!enabled || modifications?.hasLog !== true) return out
  const filterSelection = paintFilterSelection(selection)
  for (const [nodeId, citations] of Object.entries(modifications.sourceEvidenceByNode ?? {})) {
    const filtered: IngestSessionEvidence[] = []
    for (const citation of citations) {
      const remapped: IngestSessionEvidence = {
        ...citation,
        sections: remapSectionIds(citation.sections, sections)
      }
      if (citationMatchesSelection(remapped, filterSelection)) filtered.push(remapped)
    }
    if (filtered.length > 0) out.set(nodeId, filtered)
  }
  return out
}

/** A completed job remains paintable while its review branch is still active. */
export function paintableJob(
  session: IngestSession | undefined,
  branch: Branch | undefined
): boolean {
  return session !== undefined && branch?.status === 'active'
}

/** Branch-keyed paint: active branch with an ingest session or any fold rows. */
export function paintableBranch(
  branch: Branch | undefined,
  session: IngestSession | undefined,
  branchHasLog: boolean | undefined
): boolean {
  if (branch?.status !== 'active') return false
  return session !== undefined || branchHasLog === true
}

function buildTotals(
  marks: ReadonlyMap<string, PlantSessionMarkEntry>,
  relationshipMarks: ReadonlyMap<string, PlantSessionRelationshipMarkEntry>
): PlantPaintTotals {
  const totals: PlantPaintTotals = {
    added: 0,
    touched: 0,
    deleted: 0,
    relsAdded: 0,
    relsDeleted: 0
  }
  for (const entry of marks.values()) totals[entry.mark] += 1
  for (const entry of relationshipMarks.values()) {
    if (entry.mark === 'added') totals.relsAdded += 1
    else if (entry.mark === 'deleted') totals.relsDeleted += 1
    else if (entry.mark === 'touched') totals.touched += 1
  }
  return totals
}

function usePlantSessionPaintModel(): PlantSessionPaint {
  const dispatch = useAppDispatch()
  const { session, branch } = useSessionPaintBranchContext()
  const branchId = branch?.status === 'active' ? branch.id : undefined
  const selection = useAppSelector(selectSessionPaintSelection)
  const enabled = useAppSelector(selectSessionPaintEnabled)
  const setSelection = React.useCallback(
    (next: PlantPaintSelection) => {
      dispatch(setSessionPaintSelection(next))
    },
    [dispatch]
  )
  const setEnabled = React.useCallback(
    (next: boolean) => {
      dispatch(setSessionPaintEnabled(next))
    },
    [dispatch]
  )

  const fatQueryOpts = {
    refetchOnMountOrArgChange: true
  } as const

  // Identity session scopes section / job-level membership on the branch fold.
  // Resolved before the fold query so the first fetch already carries the param.
  const sectionsJobId = branchId ? session?.id : undefined

  const { data: branchWatermark } = useGetBranchIngestWriteLogWatermarkQuery(
    { branchId: branchId ?? '' },
    {
      skip: !branchId,
      refetchOnMountOrArgChange: true
    }
  )
  const {
    data: branchFold,
    refetch: refetchBranchFold,
    isUninitialized: branchFoldUninitialized
  } = useGetBranchIngestModificationsQuery(
    branchPaintModificationsArgs(branchId ?? '', sectionsJobId),
    {
      skip: !branchId,
      ...fatQueryOpts
    }
  )

  const paintable = paintableBranch(branch, session, branchFold?.hasLog)
  const jobsSummary = branchFold?.jobs ?? []
  const agentJobs = React.useMemo(() => agentJobsFromSummary(jobsSummary), [jobsSummary])
  const hasAgentEdits = agentJobs.length > 0
  const agentOnly = session === undefined && hasAgentEdits

  const isSourceJobLens = selection.kind === 'sourceJob'
  const sourceJobId = isSourceJobLens ? selection.jobId : undefined
  const isAgentLens = selection.kind === 'agent'

  const { data: sourceJobWatermark } = useGetIngestWriteLogWatermarkQuery(sourceJobId ?? '', {
    skip: !paintable || !isSourceJobLens || !sourceJobId,
    refetchOnMountOrArgChange: true
  })

  // Always load the agent fold when agent jobs contributed — badge counts need
  // entity marks (same units as All changes), not write-log batch rowCount.
  const {
    data: agentBranchModifications,
    refetch: refetchAgentBranchModifications,
    isUninitialized: agentBranchModificationsUninitialized
  } = useGetBranchIngestModificationsQuery(
    { branchId: branchId ?? '', domain: 'agent' },
    { skip: !paintable || !branchId || !hasAgentEdits, ...fatQueryOpts }
  )

  const {
    data: sourceJobModifications,
    refetch: refetchSourceJobModifications,
    isUninitialized: sourceJobModificationsUninitialized
  } = useGetIngestModificationsQuery(sourceJobId ?? '', {
    skip: !paintable || !isSourceJobLens || !sourceJobId,
    ...fatQueryOpts
  })

  const modifications: IngestModifications | undefined = isSourceJobLens
    ? sourceJobModifications
    : isAgentLens
      ? agentBranchModifications
      : branchFold

  const identityJobId = paintable ? session?.id : undefined
  const { data: sectionRows } = useListIngestSectionsQuery(identityJobId ?? '', {
    skip: !paintable || !identityJobId,
    refetchOnMountOrArgChange: true
  })

  const sections = React.useMemo(() => activeSections(sectionRows), [sectionRows])
  React.useEffect(() => {
    dispatch(bindSessionPaintBranch(branchId))
  }, [dispatch, branchId])

  const requestedSectionId = selection.kind === 'section' ? selection.sectionId : undefined
  const resolvedSelection = React.useMemo(
    () => resolvePlantPaintSelection(selection, sectionRows, jobsSummary),
    [jobsSummary, selection, sectionRows]
  )
  const resolvedSectionId =
    resolvedSelection.kind === 'section' ? resolvedSelection.sectionId : undefined
  React.useEffect(() => {
    if (
      selection.kind === 'section' &&
      sectionRows !== undefined &&
      requestedSectionId !== undefined &&
      requestedSectionId !== resolvedSectionId
    ) {
      setSelection(resolvedSelection)
      return
    }
    // Persist actor-job → agent coercion so the dropdown and query stay aligned.
    if (selection.kind === 'sourceJob' && resolvedSelection.kind === 'agent') {
      setSelection(resolvedSelection)
    }
  }, [
    requestedSectionId,
    resolvedSectionId,
    resolvedSelection,
    sectionRows,
    selection.kind,
    setSelection
  ])

  const filterSelection = React.useMemo(
    () => paintFilterSelection(resolvedSelection),
    [resolvedSelection]
  )
  const resolvedFilter = React.useMemo<PlantPaintFilter>(
    () => ({
      selection: resolvedSelection,
      setSelection,
      enabled,
      setEnabled
    }),
    [enabled, resolvedSelection, setEnabled, setSelection]
  )

  const branchChangesetArgs = React.useMemo(
    () =>
      branchPaintChangesetArgs({
        branchId: branchId ?? '',
        isAgentLens,
        ...(filterSelection.kind === 'section' ? { sectionId: filterSelection.sectionId } : {}),
        sectionsJobId
      }),
    [branchId, filterSelection, isAgentLens, sectionsJobId]
  )

  const {
    data: branchChangeset,
    refetch: refetchBranchChangeset,
    isUninitialized: branchChangesetUninitialized
  } = useGetBranchIngestChangesetQuery(branchChangesetArgs, {
    skip: !paintable || !branchId || isSourceJobLens,
    ...fatQueryOpts
  })

  const {
    data: sourceJobChangeset,
    refetch: refetchSourceJobChangeset,
    isUninitialized: sourceJobChangesetUninitialized
  } = useGetIngestChangesetQuery(
    {
      jobId: sourceJobId ?? ''
    },
    {
      skip: !paintable || !isSourceJobLens || !sourceJobId,
      ...fatQueryOpts
    }
  )

  const changeset: IngestChangeset | undefined = isSourceJobLens
    ? sourceJobChangeset
    : branchChangeset

  const branchWatermarkKey =
    branchWatermark === undefined ? undefined : `${branchWatermark.count}:${branchWatermark.maxId}`
  const lastBranchWatermarkRef = React.useRef<string | undefined>(undefined)
  React.useEffect(() => {
    lastBranchWatermarkRef.current = undefined
  }, [branchId])
  React.useEffect(() => {
    if (branchWatermarkKey === undefined) return
    const previous = lastBranchWatermarkRef.current
    lastBranchWatermarkRef.current = branchWatermarkKey
    if (previous === undefined || previous === branchWatermarkKey) return
    // RTK Query throws if refetch() runs while the hook is still skipped.
    if (!branchFoldUninitialized) void refetchBranchFold()
    if (!branchChangesetUninitialized) void refetchBranchChangeset()
    if (!agentBranchModificationsUninitialized) void refetchAgentBranchModifications()
  }, [
    agentBranchModificationsUninitialized,
    branchChangesetUninitialized,
    branchFoldUninitialized,
    branchWatermarkKey,
    refetchAgentBranchModifications,
    refetchBranchChangeset,
    refetchBranchFold
  ])

  const sourceJobWatermarkKey =
    sourceJobWatermark === undefined
      ? undefined
      : `${sourceJobWatermark.count}:${sourceJobWatermark.maxId}`
  const lastSourceJobWatermarkRef = React.useRef<string | undefined>(undefined)
  React.useEffect(() => {
    lastSourceJobWatermarkRef.current = undefined
  }, [sourceJobId])
  React.useEffect(() => {
    if (sourceJobWatermarkKey === undefined) return
    const previous = lastSourceJobWatermarkRef.current
    lastSourceJobWatermarkRef.current = sourceJobWatermarkKey
    if (previous === undefined || previous === sourceJobWatermarkKey) return
    if (!sourceJobModificationsUninitialized) void refetchSourceJobModifications()
    if (!sourceJobChangesetUninitialized) void refetchSourceJobChangeset()
  }, [
    refetchSourceJobChangeset,
    refetchSourceJobModifications,
    sourceJobChangesetUninitialized,
    sourceJobModificationsUninitialized,
    sourceJobWatermarkKey
  ])

  const branchMarkById = React.useMemo(
    () => remapMarkSections(buildMarkMap(branchFold), sectionRows),
    [branchFold, sectionRows]
  )
  const branchRelationshipMarkById = React.useMemo(
    () => remapRelationshipMarkSections(buildRelationshipMarkMap(branchFold), sectionRows),
    [branchFold, sectionRows]
  )
  const markById = React.useMemo(
    () =>
      modifications === branchFold
        ? branchMarkById
        : remapMarkSections(buildMarkMap(modifications), sectionRows),
    [branchFold, branchMarkById, modifications, sectionRows]
  )
  const relationshipMarkById = React.useMemo(
    () =>
      modifications === branchFold
        ? branchRelationshipMarkById
        : remapRelationshipMarkSections(buildRelationshipMarkMap(modifications), sectionRows),
    [branchFold, branchRelationshipMarkById, modifications, sectionRows]
  )
  const agentChangesCount = React.useMemo(() => {
    if (!hasAgentEdits) return 0
    // When the active lens already is the agent fold, reuse those maps.
    if (isAgentLens) {
      return markById.size + relationshipMarkById.size
    }
    const agentMarks = buildMarkMap(agentBranchModifications)
    const agentRelMarks = buildRelationshipMarkMap(agentBranchModifications)
    return agentMarks.size + agentRelMarks.size
  }, [
    agentBranchModifications,
    hasAgentEdits,
    isAgentLens,
    markById.size,
    relationshipMarkById.size
  ])
  const changes = React.useMemo(() => buildChangesById(changeset), [changeset])
  const ghostRels = React.useMemo(
    () => modifications?.deletedRelationships ?? [],
    [modifications?.deletedRelationships]
  )
  const relationships = modifications?.relationships
  const deletedRelationships = modifications?.deletedRelationships
  const placementRelationships = modifications?.placementRelationships
  const containmentRelationships = React.useMemo(
    () => [
      ...(relationships ?? []),
      ...(deletedRelationships ?? []),
      ...(placementRelationships ?? [])
    ],
    [deletedRelationships, placementRelationships, relationships]
  )
  const ghostsByContainerId = React.useMemo(
    () => buildGhostsByContainer(markById, containmentRelationships),
    [markById, containmentRelationships]
  )
  const scopedMarks = React.useMemo(
    () => filterMarksForSelection(markById, filterSelection, enabled),
    [enabled, filterSelection, markById]
  )
  const scopedRelationshipMarks = React.useMemo(
    () => filterRelationshipMarksForSelection(relationshipMarkById, filterSelection, enabled),
    [enabled, filterSelection, relationshipMarkById]
  )
  const rollupByContainerId = React.useMemo(
    () => buildRollups(scopedMarks, containmentRelationships),
    [containmentRelationships, scopedMarks]
  )
  const touchedRollupByContainerId = React.useMemo(
    () => buildRollups(scopedMarks, containmentRelationships, { mark: 'touched' }),
    [containmentRelationships, scopedMarks]
  )
  const deletedRollupByContainerId = React.useMemo(
    () => buildRollups(scopedMarks, containmentRelationships, { mark: 'deleted' }),
    [containmentRelationships, scopedMarks]
  )
  const equipmentRollupByContainerId = React.useMemo(
    () => buildRollups(scopedMarks, containmentRelationships, { onlyLabels: ['Equipment'] }),
    [containmentRelationships, scopedMarks]
  )
  const equipmentTouchedRollupByContainerId = React.useMemo(
    () =>
      buildRollups(scopedMarks, containmentRelationships, {
        onlyLabels: ['Equipment'],
        mark: 'touched'
      }),
    [containmentRelationships, scopedMarks]
  )
  const equipmentDeletedRollupByContainerId = React.useMemo(
    () =>
      buildRollups(scopedMarks, containmentRelationships, {
        onlyLabels: ['Equipment'],
        mark: 'deleted'
      }),
    [containmentRelationships, scopedMarks]
  )
  const totals = React.useMemo(
    () => buildTotals(scopedMarks, scopedRelationshipMarks),
    [scopedMarks, scopedRelationshipMarks]
  )
  const inactiveSectionChangeCount = React.useMemo(() => {
    const activeIds = new Set(sections.map((section) => section.id))
    let count = 0
    for (const entry of markById.values()) {
      if (entry.sections.some((sectionId) => !activeIds.has(sectionId))) count += 1
    }
    for (const entry of relationshipMarkById.values()) {
      if (entry.sections.some((sectionId) => !activeIds.has(sectionId))) count += 1
    }
    return count
  }, [markById, relationshipMarkById, sections])
  const active = paintable && modifications?.hasLog === true
  const sourceEvidence = React.useMemo(
    () =>
      active
        ? buildSourceEvidenceMap(modifications, sectionRows, resolvedSelection, enabled)
        : new Map<string, IngestSessionEvidence[]>(),
    [active, enabled, modifications, resolvedSelection, sectionRows]
  )
  const markFor = React.useCallback(
    (id: string): PlantSessionMarkView | undefined =>
      active ? applyPaintFilter(markById.get(id), filterSelection, enabled) : undefined,
    [active, enabled, filterSelection, markById]
  )
  const relationshipMarkFor = React.useCallback(
    (id: string): PlantSessionMarkView | undefined =>
      active ? applyPaintFilter(relationshipMarkById.get(id), filterSelection, enabled) : undefined,
    [active, enabled, filterSelection, relationshipMarkById]
  )

  return React.useMemo(
    () => ({
      active,
      branchId,
      jobId: identityJobId,
      session,
      agentOnly,
      hasAgentEdits,
      agentChangesCount,
      jobsSummary,
      agentJobs,
      sections,
      branchMarkById,
      branchRelationshipMarkById,
      markById,
      relationshipMarkById,
      ghostRels,
      ghostsByContainerId,
      rollupByContainerId,
      touchedRollupByContainerId,
      deletedRollupByContainerId,
      equipmentRollupByContainerId,
      equipmentTouchedRollupByContainerId,
      equipmentDeletedRollupByContainerId,
      totals,
      changes,
      sourceEvidence,
      filter: resolvedFilter,
      markFor,
      relationshipMarkFor,
      inactiveSectionChangeCount,
      truncated: modifications?.truncated === true
    }),
    [
      active,
      agentChangesCount,
      agentJobs,
      agentOnly,
      branchMarkById,
      branchRelationshipMarkById,
      branchId,
      changes,
      deletedRollupByContainerId,
      equipmentDeletedRollupByContainerId,
      equipmentRollupByContainerId,
      equipmentTouchedRollupByContainerId,
      ghostRels,
      ghostsByContainerId,
      hasAgentEdits,
      identityJobId,
      inactiveSectionChangeCount,
      jobsSummary,
      markById,
      markFor,
      modifications?.truncated,
      relationshipMarkById,
      relationshipMarkFor,
      resolvedFilter,
      rollupByContainerId,
      sections,
      session,
      sourceEvidence,
      totals,
      touchedRollupByContainerId
    ]
  )
}

export function PlantPaintProvider({
  children
}: {
  children: React.ReactNode
}): React.ReactElement {
  const value = usePlantSessionPaintModel()
  return <PlantPaintContext.Provider value={value}>{children}</PlantPaintContext.Provider>
}

export function usePlantSessionPaint(): PlantSessionPaint {
  const context = React.useContext(PlantPaintContext)
  if (!context) {
    throw new Error('usePlantSessionPaint must be used within PlantPaintProvider')
  }
  return context
}
